import { Button, type ButtonOwnProps } from '#app/components/Buttons/Button.js';
import {
  type ButtonSize,
  type ButtonVariant,
} from '#app/components/Buttons/types.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useContraPayments } from '#app/hooks/useContraPayments.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { MailSendOutlineIcon } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { getRoute } from '@contra/routes';
import { styled } from 'styled-components';

const StyledButton = styled(Button)`
  min-width: 152px;

  & > span > span {
    white-space: nowrap;
  }
`;

type NewProjectButtonProps = {
  readonly analyticsSection?: string;
  readonly block?: ButtonOwnProps['block'];
  readonly icon?: ButtonOwnProps['icon'] | null;
  readonly jobId?: null | string;
  readonly label?: string;
  readonly onMounted?: () => void;
  readonly size?: ButtonSize;
  readonly tooltip?: string;
  // responsive?: boolean;
  readonly userType: 'client' | 'independent';
  readonly variant?: ButtonVariant;
};

export const NewProjectButton = ({
  analyticsSection,
  block,
  icon = MailSendOutlineIcon,
  jobId,
  label = 'Start project',
  onMounted,
  size = 'xsmall',
  tooltip,
  userType,
  variant = 'V2Gradient',
}: NewProjectButtonProps) => {
  const { selectedUserType } = useSelectedUserType();
  const { initiateSetupPayments, userHasPaymentsSetup } = useContraPayments();
  const requirePaymentsOnboarding =
    selectedUserType === 'CONTRACTOR' && !userHasPaymentsSetup;
  const { track } = useAnalytics();

  const navigation = useNavigation();
  const trackInviteButton = () => {
    track('button', {
      action: 'tapped',
      button_name: 'invite_to_new_project',
      // reusing the same property which we use for other assets like opportunities/jobs, services etc.
      current_user_type:
        selectedUserType === 'CLIENT'
          ? 'client'
          : selectedUserType === 'CONTRACTOR'
            ? 'contractor'
            : undefined,
      section: analyticsSection,
    });
  };

  const location = useLocation();

  const { handleOnboardingRedirectionModalOpen, shouldRedirectUser } =
    useOnboardingRedirection({
      overrideUserType: userType,
      // Sending to projects so the user must
      redirectTo: location.pathname,
    });

  const handleInviteToPaidProjectClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    trackInviteButton();

    // Does user need onboarded first?
    if (shouldRedirectUser) {
      handleOnboardingRedirectionModalOpen();
      return;
    }

    if (requirePaymentsOnboarding) {
      initiateSetupPayments();
      return;
    }

    navigation.push(
      getRoute('/proposal/new', {
        search: {
          jobId: jobId ?? undefined,
        },
      }),
    );
  };

  useMount(() => {
    onMounted?.();
  });

  const button = (
    <StyledButton
      block={block}
      icon={icon ?? undefined}
      onClick={handleInviteToPaidProjectClick}
      size={size}
      variant={variant}
    >
      {label}
    </StyledButton>
  );

  if (tooltip) {
    return (
      <Tooltip
        content={tooltip}
        placement="bottom"
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};
